import { Component, OnInit, Input } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ClientEnvironmentService } from 'src/app/services/client-environment.service';
import { CorpModel } from 'src/app/models/corp';
import { UploadService } from 'src/app/services/upload.service';
import { CorpsService } from 'src/app/services/firestore';
import { SaveSteps, failureMessage } from 'src/app/utils/resilience/save-steps';
import { sluggify } from 'src/app/utils/strings/string.utils';

@Component({
  selector: 'app-add-corp-modal',
  templateUrl: './add-corp-modal.component.html',
  styleUrls: ['./add-corp-modal.component.scss'],
})
export class AddCorpModalComponent implements OnInit {
  corp: CorpModel;
  loading: boolean;

  // Adding file upload support
  logoFile: File | null;

  @Input()
  writable: boolean;

  private saveSteps: SaveSteps | null = null;

  constructor(
    private corpsService: CorpsService,
    public modal: BsModalRef,
    private authService: AuthService,
    private clientEnvironmentService: ClientEnvironmentService,
    private toaster: ToastrService,
    private uploadService: UploadService,
  ) {
    this.corp = new CorpModel();
    this.loading = false;
    this.writable = true;
  }

  async ngOnInit() {
    const user = await this.authService.currentUser;
    if (!user) {
      return;
    }
    this.corp.createdBy = user.uid;
  }

  async save(): Promise<void> {
    this.loading = true;
    // The name fixes the corp ID, so keep the ID once anything has been stored under it.
    if (!this.saveSteps?.hasProgress) {
      this.corp.id = sluggify(this.corp.label);
    }
    const steps = (this.saveSteps ??= this.buildSaveSteps());

    try {
      await steps.run();
      this.saveSteps = null;
      this.modal.hide();
      this.toaster.success('Corp created');
    } catch (error) {
      this.toaster.error(failureMessage(error));
    } finally {
      this.loading = false;
    }
  }

  private buildSaveSteps(): SaveSteps {
    return new SaveSteps([
      {
        key: 'logo',
        label: 'uploading the logo',
        run: async () => {
          if (!this.logoFile) {
            return;
          }
          this.corp.logo = await firstValueFrom(this.uploadService.upload('corps/' + this.corp.id + '/logo', this.logoFile));
        },
      },
      {
        key: 'corp',
        label: 'saving the organization',
        run: () => this.corpsService.addCorpWithEnvironment(this.corp, this.clientEnvironmentService.generateInitialEnvironment()),
      },
    ]);
  }
}
